import { Navigate, type RouteObject, useLocation, useParams } from "react-router";
import type { AppModule, RouteHandle } from "../types";

function InviteAlias() {
  const { token = "" } = useParams();
  const { search, hash } = useLocation();
  return <Navigate replace to={`/invitation/${encodeURIComponent(token)}/join-workspace${search}${hash}`} />;
}

function ToLogin() {
  const { search } = useLocation();
  return <Navigate replace to={`/login${search}`} />;
}

function ToSignup() {
  return <Navigate replace to="/signup" />;
}

const open: RouteHandle = { guestOnly: false };

const authRoutes: RouteObject[] = [
  {
    path: "/login",
    handle: { title: "Log in" },
    lazy: () => import("./LoginPage").then((m) => ({ Component: m.LoginPage })),
  },
  {
    path: "/signup",
    handle: { title: "Sign up" },
    lazy: () => import("./SignupPage").then((m) => ({ Component: m.SignupPage })),
  },
  {
    path: "/:lang/login",
    handle: { title: "Log in" },
    lazy: () =>
      Promise.all([import("./LoginPage"), import("./LangRoute")]).then(([m, l]) => ({
        Component: l.withLang(m.LoginPage),
      })),
  },
  {
    path: "/:lang/signup",
    handle: { title: "Sign up" },
    lazy: () =>
      Promise.all([import("./SignupPage"), import("./LangRoute")]).then(([m, l]) => ({
        Component: l.withLang(m.SignupPage),
      })),
  },
  {
    path: "/verify-email-address",
    handle: { ...open, title: "Confirm email" },
    lazy: () => import("./EmailVerificationPages").then((m) => ({ Component: m.VerifyEmailAddressPage })),
  },
  {
    path: "/email-verification/:code/:email",
    handle: { ...open, title: "Confirm email" },
    lazy: () => import("./EmailVerificationPages").then((m) => ({ Component: m.EmailVerificationLanding })),
  },
  {
    path: "/email-verification/confirm/:code/:email",
    handle: { ...open, title: "Confirm email" },
    lazy: () => import("./EmailVerificationPages").then((m) => ({ Component: m.EmailVerificationConfirmPage })),
  },
  {
    path: "/email-verification-status/:email",
    handle: { ...open, title: "Confirm email" },
    lazy: () => import("./EmailVerificationPages").then((m) => ({ Component: m.EmailVerificationStatusPage })),
  },
  // Clockify's OTP signup steps; the clone verifies by emailed link (routes.md 1.9).
  ...["/signup/otp", "/signup/confirm-identity"].map(
    (path): RouteObject => ({ path, handle: open, element: <Navigate replace to="/verify-email-address" /> }),
  ),
  {
    path: "/forgot-password",
    handle: { title: "Forgot password" },
    lazy: () => import("./ForgotPasswordPage").then((m) => ({ Component: m.ForgotPasswordPage })),
  },
  {
    path: "/reset-password/:token",
    handle: { ...open, title: "Create new password" },
    lazy: () => import("./ResetPasswordPage").then((m) => ({ Component: m.ResetPasswordPage })),
  },
  {
    path: "/invitation/:token/join-workspace",
    handle: { ...open, title: "Join workspace" },
    lazy: () => import("./InvitePage").then((m) => ({ Component: m.InvitePage })),
  },
  { path: "/invite/:token", handle: open, element: <InviteAlias /> },
  // OTP/SSO/subdomain flows are not supported; send them to the password login (routes.md 1.9).
  ...["/login/otp", "/login/otp/confirm-identity", "/login/confirm-identity", "/login/sso", "/login-owner"].map(
    (path): RouteObject => ({ path, handle: open, element: <ToLogin /> }),
  ),
  { path: "/account-not-found", handle: open, element: <ToSignup /> },
  {
    path: "/create-ws",
    handle: { ...open, title: "Create workspace" },
    lazy: () => import("./CreateWorkspacePage").then((m) => ({ Component: m.CreateWorkspacePage })),
  },
];

export const shellAuthModule: AppModule = {
  name: "shell-auth",
  routes: {
    auth: authRoutes,
    public: [
      {
        path: "/unsubscribe",
        handle: { title: "Unsubscribe" },
        lazy: () => import("./UnsubscribePage").then((m) => ({ Component: m.UnsubscribePage })),
      },
    ],
    shell: [
      {
        path: "/workspaces",
        handle: { title: "Workspaces" },
        lazy: () => import("./WorkspacesPage").then((m) => ({ Component: m.WorkspacesPage })),
      },
    ],
    handoff: [
      {
        path: "/logout",
        handle: { title: "Log out" },
        lazy: () => import("./LogoutPage").then((m) => ({ Component: m.LogoutPage })),
      },
    ],
  },
};
